import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ContactMailOutlined,
  DeleteOutline,
  Visibility,
  SearchOutlined,
  PersonOutlined,
  EmailOutlined,
  MessageOutlined,
  CalendarTodayOutlined,
  MarkEmailReadOutlined,
} from '@mui/icons-material';

const ContactUs = () => {
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [stats, setStats] = useState({
    totalMessages: 0,
    recentMessages: 0,
  });
  const server_url = process.env.REACT_APP_API_URI;

  useEffect(() => {
    fetch(`${server_url}/contactUs/all`)
      .then((response) => response.json())
      .then((data) => {
        console.log('API Response:', data);
        if (data.data && Array.isArray(data.data)) {
          const contactData = data.data;
          setContent(contactData);
          setFilteredContent(contactData);

          // Calculate stats
          const now = new Date();
          const sevenDaysAgo = new Date(now.setDate(now.getDate() - 7));
          const recentCount = contactData.filter(
            (c) => new Date(c.createdAt) > sevenDaysAgo,
          ).length;

          setStats({
            totalMessages: contactData.length,
            recentMessages: recentCount,
          });
        } else {
          throw new Error('Invalid data format');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setError('Failed to fetch Content.');
        setLoading(false);
      });
  }, [server_url]);

  useEffect(() => {
    let filtered = [...content];

    if (searchTerm) {
      filtered = filtered.filter(
        (c) =>
          c?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c?.message?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredContent(filtered);
  }, [searchTerm, content]);

  const handleDelete = async (id) => {
    if (
      window.confirm('Are you sure you want to delete this Contact Us Detail?')
    ) {
      try {
        const response = await fetch(`${server_url}/contactUs/delete/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Failed to delete Contact Us Detail.');
        }

        setContent((prevContent) =>
          prevContent.filter((contact) => contact._id !== id),
        );
      } catch (error) {
        setError('Error deleting contact.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Pagination
  const totalPages = Math.ceil(filteredContent.length / limit);
  const startIndex = (currentPage - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedContent = filteredContent.slice(startIndex, endIndex);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Contact Messages
        </h1>
        <p className="text-gray-600">
          View and manage user contact form submissions
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-pink-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Total Messages
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalMessages}
              </h3>
            </div>
            <div className="bg-pink-100 p-3 rounded-full">
              <ContactMailOutlined className="text-pink-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Recent (7 days)
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.recentMessages}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <MarkEmailReadOutlined className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="relative">
          <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="font-medium text-gray-700">Show:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[10, 20, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="text-gray-600">entries</span>
        </div>

        <div className="text-gray-600">
          Showing {filteredContent.length > 0 ? startIndex + 1 : 0} to{' '}
          {Math.min(endIndex, filteredContent.length)} of{' '}
          {filteredContent.length}{' '}
          {filteredContent.length !== content.length &&
            `(filtered from ${content.length})`}
        </div>
      </div>

      {/* Contact Messages Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-500 font-medium">{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold">#</th>
                  <th className="py-4 px-6 text-left font-semibold">
                    Contact Details
                  </th>
                  <th className="py-4 px-6 text-left font-semibold">
                    Message Preview
                  </th>
                  <th className="py-4 px-6 text-left font-semibold">Date</th>
                  <th className="py-4 px-6 text-center font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedContent.length > 0 ? (
                  paginatedContent.map((contact, index) => (
                    <tr
                      key={contact._id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <span className="text-gray-600 font-semibold">
                          {startIndex + index + 1}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {contact.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <PersonOutlined
                                className="text-gray-400"
                                fontSize="small"
                              />
                              <p className="font-semibold text-gray-800">
                                {contact.name || 'Anonymous'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <EmailOutlined
                                className="text-gray-400"
                                fontSize="small"
                              />
                              <p className="text-sm text-gray-600">
                                {contact.email || 'No email'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-start gap-2">
                          <MessageOutlined
                            className="text-gray-400 mt-0.5"
                            fontSize="small"
                          />
                          <p className="text-gray-700 text-sm line-clamp-2">
                            {contact.message || 'No message provided'}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <CalendarTodayOutlined
                            className="text-gray-400"
                            fontSize="small"
                          />
                          <span className="text-gray-600 text-sm">
                            {formatDate(contact.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/admin/contactUs/view/${contact._id}`}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Visibility fontSize="small" />
                          </Link>
                          <button
                            onClick={() => handleDelete(contact._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Message"
                          >
                            <DeleteOutline fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <ContactMailOutlined className="text-gray-400 text-5xl" />
                        <p className="text-gray-500 text-lg font-medium">
                          {searchTerm
                            ? 'No messages match your search'
                            : 'No contact messages found'}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {searchTerm
                            ? 'Try adjusting your search term'
                            : 'Contact messages will appear here when users submit the contact form'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
